import React, { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase.js';
import { toast } from 'react-toastify';

const PLATE_TYPE_LABELS = {
  resident: 'Resident',
  owner_temp: 'Guest (owner)',
  admin_temp: 'Guest (admin)',
};

const METHOD_LABELS = {
  lpr: 'LPR camera',
  manual: 'Manual open',
  vehicle_sighting: 'Gate camera sighting',
  vehicle_plate: 'Auto-open (plate)',
  vehicle_vision: 'Auto-open (recognized vehicle)',
};

function formatAccessMethod(method) {
  return METHOD_LABELS[method] || method || 'Event';
}

function GateAccessPage({ user, isAdmin, isMobile }) {
  const [lots, setLots] = useState([]);
  const [plates, setPlates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [selectedLotId, setSelectedLotId] = useState('');
  const [newPlate, setNewPlate] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState('resident');
  const [guestDays, setGuestDays] = useState(7);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const getGatePlates = httpsCallable(functions, 'getGatePlates');
      const getGateAccessLog = httpsCallable(functions, 'getGateAccessLog');
      const [platesRes, logsRes] = await Promise.all([
        getGatePlates(),
        getGateAccessLog({ limit: 30 }),
      ]);
      const plateData = platesRes.data || {};
      setLots(plateData.lots || []);
      setPlates(plateData.plates || []);
      setLogs(logsRes.data?.logs || []);
      if (!selectedLotId && plateData.lots?.length) {
        setSelectedLotId(plateData.lots[0].lotId);
      }
    } catch (err) {
      console.error('Gate access load error:', err);
      toast.error(err.message || 'Failed to load gate access data');
    } finally {
      setLoading(false);
    }
  }, [selectedLotId]);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  useEffect(() => {
    if (!user) return undefined;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadData();
    }, 30000);
    return () => clearInterval(interval);
  }, [user, loadData]);

  const handleOpenGate = async () => {
    setOpening(true);
    try {
      const openGateFn = httpsCallable(functions, 'openGate');
      const result = await openGateFn({ lotId: selectedLotId || undefined });
      const data = result.data || {};
      toast.success(data.message || 'Gate opened');
    } catch (err) {
      console.error('Open gate error:', err);
      toast.error(err.message || 'Failed to open gate');
    } finally {
      setOpening(false);
      loadData();
    }
  };

  const handleAddPlate = async (e) => {
    e.preventDefault();
    if (!newPlate.trim()) {
      toast.error('Enter a license plate');
      return;
    }
    if (!selectedLotId) {
      toast.error('Select a lot');
      return;
    }
    setSaving(true);
    try {
      const upsertGatePlate = httpsCallable(functions, 'upsertGatePlate');
      await upsertGatePlate({
        lotId: selectedLotId,
        plate: newPlate.trim(),
        type: newType,
        label: newLabel.trim() || undefined,
        days: newType !== 'resident' ? guestDays : undefined,
      });
      toast.success('Plate saved');
      setNewPlate('');
      setNewLabel('');
      setNewType('resident');
      await loadData();
    } catch (err) {
      console.error('Add plate error:', err);
      toast.error(err.message || 'Failed to add plate');
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePlate = async (plateId) => {
    if (!window.confirm('Remove this plate from gate access?')) return;
    try {
      const removeGatePlate = httpsCallable(functions, 'removeGatePlate');
      await removeGatePlate({ plateId });
      toast.success('Plate removed');
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to remove plate');
    }
  };

  return (
    <div className={`polls-page gate-access-page ${isMobile ? 'polls-page-mobile' : ''}`}>
      <h2 className={`polls-title ${isMobile ? 'polls-title-mobile' : ''}`}>Gate Access</h2>
      <p className="gate-access-intro">
        Register license plates for automatic entry and use the button below to open the gate from your phone or computer.
      </p>

      {loading ? (
        <div className="gate-access-loading" role="status" aria-live="polite" aria-busy="true">
          <div className="gate-access-spinner" aria-hidden="true" />
          <p>Loading gate access…</p>
        </div>
      ) : (
      <>
      <section className="gate-access-section">
        <h3>Open Gate Now</h3>
        {lots.length > 1 ? (
          <label style={{ display: 'block', marginBottom: 8 }}>
            Lot
            <select
              value={selectedLotId}
              onChange={(e) => setSelectedLotId(e.target.value)}
              style={{ display: 'block', width: '100%', maxWidth: 320, marginTop: 4, padding: 8 }}
            >
              {lots.map((lot) => (
                <option key={lot.lotId} value={lot.lotId}>
                  {lot.tract || lot.lotId}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <button
          type="button"
          onClick={handleOpenGate}
          disabled={opening || (!lots.length && !isAdmin)}
          style={{
            padding: '12px 24px',
            fontSize: 16,
            fontWeight: 600,
            backgroundColor: '#1a3c34',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: opening ? 'wait' : 'pointer',
          }}
        >
          {opening ? 'Opening…' : 'Open Gate'}
        </button>
        {!lots.length && !isAdmin ? (
          <p style={{ color: '#b45309', marginTop: 8 }}>No lot is linked to your account. Contact the HOA office.</p>
        ) : null}
      </section>

      <section className="gate-access-section">
        <h3>Registered Plates</h3>
        {lots.length > 1 ? (
          <label style={{ display: 'block', marginBottom: 12 }}>
            Manage plates for lot
            <select
              value={selectedLotId}
              onChange={(e) => setSelectedLotId(e.target.value)}
              style={{ display: 'block', width: '100%', maxWidth: 320, marginTop: 4, padding: 8 }}
            >
              {lots.map((lot) => (
                <option key={lot.lotId} value={lot.lotId}>
                  {lot.tract || lot.lotId}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <form onSubmit={handleAddPlate} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <input
              type="text"
              placeholder="License plate"
              value={newPlate}
              onChange={(e) => setNewPlate(e.target.value.toUpperCase())}
              style={{ flex: '1 1 140px', padding: 8 }}
            />
            <select value={newType} onChange={(e) => setNewType(e.target.value)} style={{ padding: 8 }}>
              <option value="resident">Resident vehicle</option>
              <option value="owner_temp">Guest pass (up to 7 days)</option>
              {isAdmin ? <option value="admin_temp">Admin guest pass (up to 30 days)</option> : null}
            </select>
            {newType !== 'resident' ? (
              <input
                type="number"
                min={1}
                max={newType === 'admin_temp' ? 30 : 7}
                value={guestDays}
                onChange={(e) => setGuestDays(Number(e.target.value))}
                style={{ width: 72, padding: 8 }}
                title="Days"
              />
            ) : null}
            <input
              type="text"
              placeholder="Label (optional)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              style={{ flex: '1 1 120px', padding: 8 }}
            />
            <button type="submit" disabled={saving} style={{ padding: '8px 16px' }}>
              {saving ? 'Saving…' : 'Add Plate'}
            </button>
          </div>
          <p style={{ fontSize: 13, color: '#555' }}>
            Up to 4 resident plates per lot. Up to 2 active guest passes per lot (7 days max).
            {isAdmin ? ' Admins may issue 30-day passes.' : ''}
          </p>
        </form>

        {plates.filter((p) => p.lotId === selectedLotId || !selectedLotId).length === 0 ? (
          <p>No plates registered yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {plates
              .filter((p) => p.lotId === selectedLotId || !selectedLotId)
              .map((plate) => (
                <li
                  key={plate.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <span>
                    <strong>{plate.displayPlate}</strong>
                    {' — '}
                    {PLATE_TYPE_LABELS[plate.type] || plate.type}
                    {plate.label ? ` (${plate.label})` : ''}
                    {plate.source === 'vehicle_group' ? ' · from Vehicle Log' : ''}
                    {plate.expiresAt ? ` · expires ${new Date(plate.expiresAt).toLocaleDateString()}` : ''}
                  </span>
                  <button type="button" onClick={() => handleRemovePlate(plate.id)} style={{ color: '#b91c1c' }}>
                    Remove
                  </button>
                </li>
              ))}
          </ul>
        )}
      </section>

      <section className="gate-access-section">
        <h3>Recent Access</h3>
        {logs.length === 0 ? (
          <p>No access events yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, fontSize: 14 }}>
            {logs.map((log) => (
              <li key={log.id} style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}>
                {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                {' · '}
                {formatAccessMethod(log.method)}
                {' · '}
                {log.result}
                {log.plate ? ` · ${log.plate}` : ''}
                {log.description ? ` · ${log.description}` : ''}
                {log.lotId ? ` · lot ${log.lotId}` : ''}
              </li>
            ))}
          </ul>
        )}
      </section>
      </>
      )}
    </div>
  );
}

export default GateAccessPage;